import React, { createRef } from "react";
import { ui } from "@/strings";
import { pageContainer } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global error boundary for the entire application.
 *
 * Catches rendering errors from any component in the tree and displays
 * a user-friendly full-page error message with a reload button.
 *
 * This prevents the dreaded "white screen of death" when React unmounts
 * the entire tree due to an uncaught error.
 */
export class GlobalErrorBoundary extends React.Component<Props, State> {
  private containerRef = createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("Global rendering error:", error, errorInfo);
  }

  componentDidUpdate(_prevProps: Props, prevState: State): void {
    // Focus the error container when error first occurs for accessibility
    if (this.state.hasError && !prevState.hasError) {
      this.containerRef.current?.focus();
    }
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className={pageContainer}>
          <div
            ref={this.containerRef}
            role="alert"
            aria-live="assertive"
            tabIndex={-1}
            className="mx-auto mt-20 max-w-2xl rounded-2xl border border-border bg-white p-8 shadow-card focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <div className="text-center">
              <h1 className="text-2xl font-bold text-text-theme-primary">
                {ui.errors.globalErrorTitle}
              </h1>
              <p className="mt-4 text-lg text-text-theme-secondary">
                {ui.errors.globalErrorDescription}
              </p>
              <button
                type="button"
                onClick={this.handleReload}
                className={`${buttonVariants.primary} mt-6`}
              >
                {ui.errors.globalErrorRefreshButton}
              </button>
            </div>
            {this.state.error && (
              <details className="mt-6">
                <summary className="cursor-pointer text-sm font-medium text-text-theme-primary hover:text-text-theme-primary">
                  {ui.errors.technicalDetails}
                </summary>
                <pre className="mt-2 overflow-x-auto rounded-lg bg-surface-secondary p-4 text-xs text-text-theme-primary">
                  {this.state.error.message}
                  {this.state.error.stack && (
                    <>
                      {"\n\n"}
                      {this.state.error.stack}
                    </>
                  )}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
